<template>
    <div id="test-app-dt">
        <Header :title="$t('model_template.title')" />
        <div class="">
            <DataTable
                :columns="userColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>

                        <div class="ppv2-flex-1"></div>
                        <button
                            class="ppv2-min-h-[40px] ppv2-flex ppv2-items-center !ppv2-text-white hover:ppv2-text-white ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-2 ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white hover:ppv2-bg-[#6b26a3] ppv2-transition-all ppv2-shadow-lg poppinsRegular"
                            @click="handlePage"
                            type="button"
                        >
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">{{
                                $t('model_template.create')
                            }}</span>
                        </button>
                    </div>
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <a
                            :href="`/back-office/v2/model_template/edit/${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_model_template_edit.actions.modify.title')"
                        >
                            <SquarePen :size="18" />
                        </a>
                        <a
                            :href="`/back-office/v2/model_template/delete/${item.id}`"
                            class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_model_template_edit.actions.delete.title')"
                        >
                            <Trash2 :size="18" />
                        </a>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script>
import Header from '../../Shared/Header.vue'
import DataTable from '../../Shared/DataTable.vue'
import { ChevronDown, SquarePen, Trash2 } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['hasEdit', 'baseUrl'],
    components: {
        DataTable,
        Header,
        Search,
        ChevronDown,
        SquarePen,
        Trash2,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 5,
            totalEntries: 0,
            searchQuery: '',
            sortKey: 'id',
            sortOrder: 'desc',
            showPerPageDropdown: false,
            searchTimeout: null,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },
            usersApiUrl: '/back-office/v2/model_template/list',
            userColumns: [
                {
                    key: 'title',
                    label: this.$t('datatable_model_template_edit.title'),
                    sortable: true,
                    headerClass: 'ppv2-w-[80%]',
                    type: 'text',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_model_template_edit.action'),
                    sortable: false,
                    headerClass: 'ppv2-w-[20%]',
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.usersApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        selectPerPage(value) {
            this.perPage = value
            this.showPerPageDropdown = false
            this.currentPage = 1
            this.fetchData()
        },

        handlePage() {
            window.location.href = `${window.location.origin}/back-office/v2/model_template/create`
        },
    },
}
</script>

<style scoped>
.btn:hover {
    color: white;
}
</style>
